import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { fetchRows } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import DateField from '../components/DateField'
import HistoryLink from '../components/HistoryLink'
import { shipTodayAuto } from '../lib/shipTime'
import { buildLsaHtml } from '../lib/lsaPdf'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function todayISO() { return shipTodayAuto() }
function curPeriod() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }
function periodLabel(p) { const [y, m] = p.split('-'); return `${MONTHS[+m - 1]} ${y}` }

// expiry status: red expired, amber <=90d, green ok, null none
function expStatus(dateStr) {
  if (!dateStr) return null
  const d = new Date(dateStr + 'T00:00:00')
  const now = new Date(todayISO() + 'T00:00:00')
  const days = Math.round((d - now) / 86400000)
  if (days < 0) return { key: 'exp', label: 'Expired', color: '#b4313c', days }
  if (days <= 90) return { key: 'soon', label: `${days}d`, color: '#8a5c0a', days }
  return { key: 'ok', label: 'OK', color: '#137050', days }
}
function fmtDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function LSA({ onNavigate }) {
  const { config } = useAuth()
  const [items, setItems] = useState([])
  const [checks, setChecks] = useState([])      // checks for current period
  const [loading, setLoading] = useState(true)
  const [cat, setCat] = useState('LSA')          // LSA | FFE
  const [groupBy, setGroupBy] = useState('location') // location | department
  const [detail, setDetail] = useState(null)
  const [period, setPeriod] = useState(curPeriod())
  const [periods, setPeriods] = useState([curPeriod()])
  const [q, setQ] = useState('')
  const [collapsed, setCollapsed] = useState({})
  const [statFilter, setStatFilter] = useState(null)
  const toggleGroup = (k) => setCollapsed(c => ({ ...c, [k]: !c[k] }))
  const isCollapsed = (k, count) => (k in collapsed) ? collapsed[k] : count >= 8
  const [crew, setCrew] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [showExport, setShowExport] = useState(false)
  const [printHtml, setPrintHtml] = useState(null)
  const [exporting, setExporting] = useState(false)
  const printRef = useRef(null)
  const exportRef = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => { if (config?.id) { load(); loadCrew() } }, [config?.id])

  async function load() {
    setLoading(true)
    const { data: its } = await supabase.from('lsa_items').select('*').eq('client_id', config.id).order('position', { ascending: true }).order('created_at', { ascending: true })
    setItems(its || [])
    const ids = (its || []).map(i => i.id)
    if (ids.length) {
      const { data: allChk } = await supabase.from('lsa_checks').select('period').in('item_id', ids)
      setPeriods([...new Set([curPeriod(), ...((allChk || []).map(c => c.period))])].sort().reverse())
      const { data: chk } = await supabase.from('lsa_checks').select('*').eq('period', period).in('item_id', ids)
      setChecks(chk || [])
    } else { setChecks([]); setPeriods([curPeriod()]) }
    setLoading(false)
  }
  async function loadCrew() {
    try {
      const rows = await fetchRows('crew')
      const gv = v => (!v ? '' : (typeof v === 'object' && v.value) ? v.value : String(v))
      const names = (rows || []).map(r => `${gv(r['First Name']).trim()} ${gv(r['Last Name']).trim()}`.trim()).filter(Boolean)
      setCrew([...new Set(names)])
    } catch (e) { setCrew([]) }
  }

  async function loadChecks(per) {
    const ids = items.map(i => i.id)
    if (!ids.length) { setChecks([]); return }
    const { data: chk } = await supabase.from('lsa_checks').select('*').eq('period', per).in('item_id', ids)
    setChecks(chk || [])
  }
  useEffect(() => { if (items.length) loadChecks(period) }, [period])

  const checkFor = (itemId) => checks.find(c => c.item_id === itemId)

  function taskLabel(item) {
    const name = (item.name || '').trim()
    const type = (item.type || '').trim()
    const loc  = (item.location || '').trim()
    const parts = []
    if (type) parts.push(type)
    if (name && name.toLowerCase() !== type.toLowerCase()) parts.push(name)
    let out = parts.join(' \u2014 ') || 'LSA/FFE item'
    if (loc && loc.toLowerCase() !== name.toLowerCase() && loc.toLowerCase() !== type.toLowerCase()) out += ' (' + loc + ')'
    return out
  }

  async function setCheck(item, status) {
    const existing = checkFor(item.id)
    // cycle: none -> ok -> defect -> na -> none  (when tapping same); explicit status when passed
    const optimistic = { item_id: item.id, period, check_date: todayISO(), status, checked_by: config?.crewName || config?.email || null }
    if (existing) {
      setChecks(cs => cs.map(c => c.item_id === item.id ? { ...c, status } : c))
      await supabase.from('lsa_checks').update({ status, check_date: todayISO() }).eq('id', existing.id)
    } else {
      setChecks(cs => [...cs, optimistic])
      const { data } = await supabase.from('lsa_checks').insert({ item_id: item.id, period, check_date: todayISO(), status, checked_by: config?.crewName || config?.email || null }).select().single()
      if (data) setChecks(cs => cs.map(c => (c.item_id === item.id && !c.id) ? data : c))
    }
    if (status === 'caution' || status === 'defect') {
      const listId = await ensureLSAList()
      if (listId) {
        const label = status === 'caution' ? '⚠ Caution' : '! Defect'
        const nm = taskLabel(item)
        const loc = ''
        const priorNote = (existing && existing.notes) || item.notes || null
        await supabase.from('todo_items').insert({ list_id: listId, text: label + ': ' + nm + loc, priority: status === 'defect' ? 3 : 2, done: false, notes: priorNote })
      }
    }
  }
  async function clearCheck(item) {
    const existing = checkFor(item.id)
    if (!existing) return
    setChecks(cs => cs.filter(c => c.item_id !== item.id))
    if (existing.id) await supabase.from('lsa_checks').delete().eq('id', existing.id)
  }

  async function ensureLSAList() {
    // one shared list per name: found or made by the database (todo_list_for, sql/test-fixes-2.sql)
    const { data } = await supabase.rpc('todo_list_for', { p_client: config.id, p_name: 'LSA / FFE' })
    return data || null
  }

  async function saveNote(checkId, notes) {
    setChecks(cs => cs.map(c => c.id === checkId ? { ...c, notes } : c))
    await supabase.from('lsa_checks').update({ notes }).eq('id', checkId)
    const chk = checks.find(c => c.id === checkId)
    if (!chk) return
    const item = items.find(i => i.id === chk.item_id)
    if (!item) return
    // mirror the note onto the item record
    setItems(its => its.map(x => x.id === item.id ? { ...x, notes } : x))
    setDetail(d => d && d.id === item.id ? { ...d, notes } : d)
    await supabase.from('lsa_items').update({ notes }).eq('id', item.id)
    // mirror onto the open To Do task raised for this item
    if (chk.status === 'caution' || chk.status === 'defect') {
      const listId = await ensureLSAList()
      if (listId) {
        const label = chk.status === 'caution' ? '\u26a0 Caution' : '! Defect'
        const nm = taskLabel(item)
        const loc = ''
        await supabase.from('todo_items')
          .update({ notes })
          .eq('list_id', listId)
          .eq('text', label + ': ' + nm + loc)
          .eq('done', false)
      }
    }
  }

  async function updateItem(id, patch) {
    setItems(its => its.map(x => x.id === id ? { ...x, ...patch } : x))
    setDetail(d => d && d.id === id ? { ...d, ...patch } : d)
    await supabase.from('lsa_items').update(patch).eq('id', id)
  }
  async function deleteItem(id) {
    const it = items.find(x => x.id === id), label = '"' + ((it && it.name) || 'item') + '"'
    if (!confirm('Delete ' + label + ' and its check history? You can undo it for 10 seconds.')) return
    setItems(its => its.filter(x => x.id !== id))
    if (detail?.id === id) setDetail(null)
    // Undo puts the item back with its check history
    try { await deleteWithUndo(supabase, { table: 'lsa_items', id, label, children: [{ table: 'lsa_checks', column: 'item_id' }], onUndone: () => load() }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); load() }
  }
  async function addItem(fields) {
    const { data } = await supabase.from('lsa_items').insert({ client_id: config.id, position: items.length, ...fields }).select().single()
    if (data) { setItems(its => [...its, data]); setShowAdd(false); setDetail(data) }
  }

  // filter to category + search
  let shown = items.filter(i => (i.category || 'LSA') === cat)
  if (q.trim()) {
    const s = q.toLowerCase()
    shown = shown.filter(i => [i.name, i.type, i.location, i.serial, i.manufacturer].some(v => (v || '').toLowerCase().includes(s)))
  }
  const isPast = period !== curPeriod()
  // group
  const groups = {}
  if (statFilter === 'ok') shown = shown.filter(i => checkFor(i.id)?.status === 'ok')
  else if (statFilter === 'caution') shown = shown.filter(i => checkFor(i.id)?.status === 'caution')
  else if (statFilter === 'defect') shown = shown.filter(i => checkFor(i.id)?.status === 'defect')
  else if (statFilter === 'unchecked') shown = shown.filter(i => !checkFor(i.id))
  else if (statFilter === 'expiring') shown = shown.filter(i => { const e = expStatus(i.expiry_date) || expStatus(i.next_service); return e && e.key === 'soon' })
  else if (statFilter === 'expired') shown = shown.filter(i => { const e = expStatus(i.expiry_date) || expStatus(i.next_service); return e && e.key === 'exp' })
  shown.forEach(i => { const k = (groupBy === 'type' ? i.type : groupBy === 'department' ? i.department : i.location) || 'Unassigned'; (groups[k] = groups[k] || []).push(i) })
  const groupKeys = Object.keys(groups).sort()

  // attention stats (whole category)
  const catItems = items.filter(i => (i.category || 'LSA') === cat)
  const expiredCount = catItems.filter(i => { const s = expStatus(i.expiry_date) || expStatus(i.next_service); return s && s.key === 'exp' }).length
  const soonCount = catItems.filter(i => { const s = expStatus(i.expiry_date) || expStatus(i.next_service); return s && s.key === 'soon' }).length
  const okCount = catItems.filter(i => checkFor(i.id)?.status === 'ok').length
  const cautionCount = catItems.filter(i => checkFor(i.id)?.status === 'caution').length
  const defectCount = catItems.filter(i => checkFor(i.id)?.status === 'defect').length
  const uncheckedCount = catItems.filter(i => !checkFor(i.id)).length

  const GROUP_LABEL = { location: 'Location', department: 'Department', type: 'Type' }
  const FILTER_LABEL = { ok: 'Checked OK', caution: 'Caution', defect: 'Defect', unchecked: 'Not checked', expiring: 'Expiring \u226490d', expired: 'Expired' }

  async function exportPdf(mode) {
    setShowExport(false)
    if (exporting) return
    setExporting(true)
    try {
      const bits = [cat, 'grouped by ' + (GROUP_LABEL[groupBy] || groupBy).toLowerCase()]
      if (mode === 'check') bits.push(periodLabel(period))
      if (statFilter) bits.push('filter: ' + (FILTER_LABEL[statFilter] || statFilter))
      if (q.trim()) bits.push('search: \u201c' + q.trim() + '\u201d')
      bits.push(shown.length + ' of ' + catItems.length + ' items')
      const html = buildLsaHtml({
        vessel: config?.vessel_name || config?.vessel || '',
        cat, mode,
        periodText: periodLabel(period),
        groupByLabel: GROUP_LABEL[groupBy] || groupBy,
        groupKeys, groups,
        checkOf: (it) => checkFor(it.id),
        counts: { total: catItems.length, ok: okCount, caution: cautionCount, defect: defectCount, unchecked: uncheckedCount, soon: soonCount, expired: expiredCount },
        filterNote: bits.join('  \u00b7  '),
        today: todayISO(),
      })
      setPrintHtml(html)
      await new Promise(r => setTimeout(r, 120))
      const el = printRef.current
      if (!el) throw new Error('print target missing')
      const html2pdf = (await import('html2pdf.js')).default
      const safe = (config?.vessel_name || 'Vessel').replace(/[^A-Za-z0-9]+/g, '_')
      const name = safe + '_' + cat + '_' + (mode === 'check' ? periodLabel(period).replace(/ /g, '_') + '_Check' : 'Register') + '.pdf'
      await html2pdf().set({
        margin: [8, 8, 10, 8],
        filename: name,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', height: el.scrollHeight, windowHeight: el.scrollHeight },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(el).save()
    } catch (e) {
      console.error('LSA PDF export failed', e)
      alert('PDF export failed: ' + (e?.message || e))
    } finally {
      setPrintHtml(null)
      setExporting(false)
    }
  }

  const STATUS = { ok: { label: '✓', color: '#137050', bg: 'rgba(93,186,142,.15)' }, caution: { label: '⚠', color: '#8a5c0a', bg: 'rgba(201,147,58,.15)' }, defect: { label: '!', color: '#b4313c', bg: 'rgba(224,112,112,.15)' }, na: { label: '–', color: '#5b6878', bg: 'rgba(138,151,166,.12)' } }

  return (
    <div style={s.page}>
      <div style={s.main}>
        {/* header: tabs + attention */}
        <div style={s.head}>
          <div style={s.tabs}>
            {['LSA', 'FFE'].map(c => (
              <button key={c} style={{ ...s.tab, ...(cat === c ? s.tabOn : {}) }} onClick={() => { setCat(c); setDetail(null) }}>{c}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button style={{ ...s.roundsBtn, opacity: exporting ? .6 : 1 }} disabled={exporting} onClick={() => setShowExport(o => !o)}>
                {exporting ? 'Building PDF\u2026' : 'Export PDF \u25be'}
              </button>
              {showExport && (
                <div style={s.expMenu}>
                  <div style={s.expOpt} onClick={() => exportPdf('check')}>
                    <div style={s.expOptT}>Monthly check record</div>
                    <div style={s.expOptS}>{periodLabel(period)} · status, checker, sign-off</div>
                  </div>
                  <div style={s.expOpt} onClick={() => exportPdf('register')}>
                    <div style={s.expOptT}>Equipment register</div>
                    <div style={s.expOptS}>Full details · no check state</div>
                  </div>
                </div>
              )}
            </div>
            <button style={s.addBtn} onClick={() => setShowAdd(true)}>+ Add item</button>
          </div>
        </div>

        {/* attention strip */}
        <div style={s.strip}>
          <Stat n={okCount} total={catItems.length} label={`✓ OK · ${periodLabel(period)}`} color="#137050" onClick={() => setStatFilter(f => f === 'ok' ? null : 'ok')} active={statFilter === 'ok'} />
          <Stat n={cautionCount} label="⚠ Caution" color={cautionCount ? '#8a5c0a' : '#137050'} onClick={() => setStatFilter(f => f === 'caution' ? null : 'caution')} active={statFilter === 'caution'} />
          <Stat n={defectCount} label="! Defect" color={defectCount ? '#b4313c' : '#137050'} onClick={() => setStatFilter(f => f === 'defect' ? null : 'defect')} active={statFilter === 'defect'} />
          <Stat n={uncheckedCount} label="Unchecked" color={uncheckedCount ? '#8a5c0a' : '#137050'} onClick={() => setStatFilter(f => f === 'unchecked' ? null : 'unchecked')} active={statFilter === 'unchecked'} />
          <Stat n={soonCount} label="Expiring ≤90d" color={soonCount ? '#8a5c0a' : '#137050'} onClick={() => setStatFilter(f => f === 'expiring' ? null : 'expiring')} active={statFilter === 'expiring'} />
          <Stat n={expiredCount} label="Expired" color={expiredCount ? '#b4313c' : '#137050'} onClick={() => setStatFilter(f => f === 'expired' ? null : 'expired')} active={statFilter === 'expired'} />
        </div>

        {/* controls */}
        <div style={s.controls}>
          <input style={s.search} placeholder="Search name, serial, location…" value={q} onChange={e => setQ(e.target.value)} />
          <div style={s.grpToggle}>
            <button style={{ ...s.grpBtn, ...(groupBy === 'location' ? s.grpOn : {}) }} onClick={() => setGroupBy('location')}>By location</button>
            <button style={{ ...s.grpBtn, ...(groupBy === 'department' ? s.grpOn : {}) }} onClick={() => setGroupBy('department')}>By dept</button>
            <button style={{ ...s.grpBtn, ...(groupBy === 'type' ? s.grpOn : {}) }} onClick={() => setGroupBy('type')}>By type</button>
          </div>
          <LsaDropdown value={period} onChange={setPeriod}
            options={periods.map(pp => ({ value: pp, label: periodLabel(pp) + (pp === curPeriod() ? '' : ' · archive') }))} />
          {onNavigate && <button style={s.roundsBtn} onClick={() => { localStorage.setItem('hc_open_checklist','IN-03 Safety Readiness Rounds (LSA / FFE)'); onNavigate('checklists'); }}>Readiness Rounds →</button>}
        </div>

        {/* groups */}
        <div style={s.groups}>
          {loading && <div style={s.empty}>Loading…</div>}
          {!loading && !shown.length && <div style={s.empty}>No {cat} items yet. Add one, or import your spreadsheet.</div>}
          {groupKeys.map(gk => (
            <div key={gk} style={s.group}>
              <div style={{ ...s.groupHead, cursor: 'pointer' }} onClick={() => toggleGroup(gk)}>
                <span style={{ ...s.chevron, transform: isCollapsed(gk, groups[gk].length) ? 'rotate(-90deg)' : 'none' }}>▾</span>
                {gk} <span style={s.groupCount}>{groups[gk].length}</span>
              </div>
              {!isCollapsed(gk, groups[gk].length) && groups[gk].map(item => {
                const chk = checkFor(item.id)
                const st = chk ? STATUS[chk.status] : null
                const exp = expStatus(item.expiry_date) || expStatus(item.next_service)
                return (
                  <div key={item.id} style={{ ...s.row, ...(detail?.id === item.id ? s.rowActive : {}) }} onClick={() => setDetail(item)}>
                    <div style={s.checkCluster} onClick={e => e.stopPropagation()}>
                      <button title="OK" disabled={isPast} style={{ ...s.ck, ...(chk?.status === 'ok' ? { background: STATUS.ok.bg, borderColor: STATUS.ok.color, color: STATUS.ok.color } : {}), ...(isPast ? { cursor: 'default', opacity: chk?.status === 'ok' ? 1 : .3 } : {}) }} onClick={isPast ? undefined : () => chk?.status === 'ok' ? clearCheck(item) : setCheck(item, 'ok')}>✓</button>
                      <button title="Caution" disabled={isPast} style={{ ...s.ck, ...(chk?.status === 'caution' ? { background: STATUS.caution.bg, borderColor: STATUS.caution.color, color: STATUS.caution.color } : {}), ...(isPast ? { cursor: 'default', opacity: chk?.status === 'caution' ? 1 : .3 } : {}) }} onClick={isPast ? undefined : () => chk?.status === 'caution' ? clearCheck(item) : setCheck(item, 'caution')}>⚠</button>
                      <button title="Defect" disabled={isPast} style={{ ...s.ck, ...(chk?.status === 'defect' ? { background: STATUS.defect.bg, borderColor: STATUS.defect.color, color: STATUS.defect.color } : {}), ...(isPast ? { cursor: 'default', opacity: chk?.status === 'defect' ? 1 : .3 } : {}) }} onClick={isPast ? undefined : () => chk?.status === 'defect' ? clearCheck(item) : setCheck(item, 'defect')}>!</button>
                    </div>
                    <div style={s.rowMain}>
                      <div style={s.rowName}>{item.name || item.type || '(unnamed)'}</div>
                      <div style={s.rowMeta}>
                        {item.type && item.name && <span>{item.type}</span>}
                        {item.serial && <span>· {item.serial}</span>}
                        {item.size && <span>· {item.size}</span>}
                      </div>
                      {isPast && chk && <div style={s.archMeta}>{chk.status}{chk.check_date ? ' · ' + fmtDate(chk.check_date) : ''}{chk.checked_by ? ' · ' + chk.checked_by : ''}{chk.notes ? ' · ' + chk.notes : ''}</div>}
                      {!isPast && chk && (chk.status === 'caution' || chk.status === 'defect') && <input key={chk.id} style={s.noteIn} defaultValue={chk.notes || ''} placeholder="Add note…" onClick={e => e.stopPropagation()} onBlur={e => saveNote(chk.id, e.target.value)} />}
                    </div>
                    {exp && <span style={{ ...s.expChip, color: exp.color, borderColor: exp.color }}>{exp.key === 'ok' ? fmtDate(item.expiry_date || item.next_service) : exp.label}</span>}
                    <button style={s.rowDel} onClick={e => { e.stopPropagation(); deleteItem(item.id) }}>×</button>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* detail panel */}
      {detail && (
        <div style={s.detail}>
          <div style={s.detailHead}>
            <span style={s.detailKicker}>{detail.category} · {detail.type || 'Item'}</span>
            <span style={{ marginLeft: 'auto', marginRight: 10 }}><HistoryLink table="lsa_items" rowId={detail.id} label={detail.name} /></span>
            <button style={s.detailClose} onClick={() => setDetail(null)}>×</button>
          </div>
          <input style={s.dName} value={detail.name || ''} placeholder="Item name" onChange={e => updateItem(detail.id, { name: e.target.value })} />
          <Field label="Type"><input style={s.in} value={detail.type || ''} onChange={e => updateItem(detail.id, { type: e.target.value })} /></Field>
          <Field label="Location"><input style={s.in} value={detail.location || ''} onChange={e => updateItem(detail.id, { location: e.target.value })} /></Field>
          <Field label="Department"><input style={s.in} value={detail.department || ''} placeholder="Deck / Engineering / Interior" onChange={e => updateItem(detail.id, { department: e.target.value })} /></Field>
          <div style={s.row2}>
            <Field label="Serial"><input style={s.in} value={detail.serial || ''} onChange={e => updateItem(detail.id, { serial: e.target.value })} /></Field>
            <Field label="Size"><input style={s.in} value={detail.size || ''} onChange={e => updateItem(detail.id, { size: e.target.value })} /></Field>
          </div>
          <Field label="Manufacturer"><input style={s.in} value={detail.manufacturer || ''} onChange={e => updateItem(detail.id, { manufacturer: e.target.value })} /></Field>
          <div style={s.row2}>
            <Field label="Expiry"><DateField value={detail.expiry_date || ''} onChange={v => updateItem(detail.id, { expiry_date: v || null })} style={s.in} /></Field>
            <Field label="Next service"><DateField value={detail.next_service || ''} onChange={v => updateItem(detail.id, { next_service: v || null })} style={s.in} /></Field>
          </div>
          <Field label="Last service"><DateField value={detail.service_date || ''} onChange={v => updateItem(detail.id, { service_date: v || null })} style={s.in} /></Field>
          <Field label="Notes"><textarea style={s.area} value={detail.notes || ''} onChange={e => updateItem(detail.id, { notes: e.target.value })} /></Field>
          <button style={s.delBtn} onClick={() => deleteItem(detail.id)}>Delete item</button>
        </div>
      )}

      {printHtml && (
        <div style={s.printWrap}>
          <div ref={printRef} dangerouslySetInnerHTML={{ __html: printHtml }} />
        </div>
      )}

      {showAdd && <AddModal cat={cat} onClose={() => setShowAdd(false)} onSave={addItem} />}
    </div>
  )
}

function Stat({ n, total, label, color, onClick, active }) {
  return (
    <div style={{ ...s.stat, ...(onClick ? { cursor: 'pointer' } : {}), ...(active ? { border: '1px solid rgba(184,150,62,.6)', boxShadow: '0 0 0 1px rgba(184,150,62,.2)' } : {}) }} onClick={onClick}>
      <div style={{ ...s.statN, color }}>{n}{total != null ? <span style={s.statTot}>/{total}</span> : null}</div>
      <div style={s.statL}>{label}</div>
    </div>
  )
}
function Field({ label, children }) {
  return <div style={s.field}><label style={s.fieldLabel}>{label}</label>{children}</div>
}

function AddModal({ cat, onClose, onSave }) {
  const [f, setF] = useState({ category: cat, type: '', name: '', location: '', department: '', serial: '', size: '', manufacturer: '', expiry_date: '', next_service: '' })
  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  return (
    <div style={s.modalWrap} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.detailHead}><span style={s.detailKicker}>Add {cat} item</span><button style={s.detailClose} onClick={onClose}>×</button></div>
        <Field label="Category">
          <div style={s.grpToggle}>
            {['LSA', 'FFE'].map(c => <button key={c} style={{ ...s.grpBtn, ...(f.category === c ? s.grpOn : {}) }} onClick={() => set('category', c)}>{c}</button>)}
          </div>
        </Field>
        <Field label="Name"><input style={s.in} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
        <div style={s.row2}>
          <Field label="Type"><input style={s.in} value={f.type} placeholder="Extinguisher…" onChange={e => set('type', e.target.value)} /></Field>
          <Field label="Location"><input style={s.in} value={f.location} onChange={e => set('location', e.target.value)} /></Field>
        </div>
        <div style={s.row2}>
          <Field label="Serial"><input style={s.in} value={f.serial} onChange={e => set('serial', e.target.value)} /></Field>
          <Field label="Size"><input style={s.in} value={f.size} onChange={e => set('size', e.target.value)} /></Field>
        </div>
        <div style={s.row2}>
          <Field label="Expiry"><DateField value={f.expiry_date} onChange={v => set('expiry_date', v)} style={s.in} /></Field>
          <Field label="Next service"><DateField value={f.next_service} onChange={v => set('next_service', v)} style={s.in} /></Field>
        </div>
        <button style={s.addBtn} onClick={() => onSave({ ...f, expiry_date: f.expiry_date || null, next_service: f.next_service || null })} disabled={!f.name && !f.type}>Add item</button>
      </div>
    </div>
  )
}

const s = {
  page: { display: 'flex', gap: 14, height: 'calc(100vh - 140px)', minHeight: 420 },
  main: { flex: 1, background: 'var(--hull, #162840)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 18, overflowY: 'auto', minWidth: 0 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  tabs: { display: 'flex', gap: 6 },
  tab: { background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', color: 'var(--muted, #8a97a6)', borderRadius: 8, padding: '7px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  tabOn: { background: 'rgba(184,150,62,.15)', color: 'var(--gold2, #d4b06a)', borderColor: 'var(--border2)' },
  addBtn: { background: 'rgba(184,150,62,.15)', color: 'var(--gold2, #d4b06a)', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  strip: { display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  stat: { flex: 1, minWidth: 120, background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '10px 14px' },
  statN: { fontSize: 22, fontWeight: 700 },
  statTot: { fontSize: 14, color: 'var(--muted, #8a97a6)', fontWeight: 400 },
  statL: { fontSize: 11, color: 'var(--muted, #8a97a6)', textTransform: 'uppercase', letterSpacing: .4, marginTop: 2 },
  controls: { display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' },
  search: { flex: 1, background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '9px 12px', color: 'var(--text, #cdd6e0)', fontSize: 13 },
  grpToggle: { display: 'flex', gap: 4 },
  grpBtn: { background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', color: 'var(--muted, #8a97a6)', borderRadius: 7, padding: '7px 12px', cursor: 'pointer', fontSize: 12 },
  grpOn: { background: 'rgba(184,150,62,.15)', color: 'var(--gold2, #d4b06a)', borderColor: 'var(--border2)' },
  groups: { display: 'flex', flexDirection: 'column', gap: 16 },
  monthSel: { background: 'var(--hull, #162840)', color: 'var(--text, #dde8f0)', border: '1px solid var(--border, rgba(184,150,62,0.2))', borderRadius: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer' },
  archMeta: { fontSize: 11, color: 'var(--gold2, #8a5c0a)', marginTop: 3, textTransform: 'capitalize' },
  noteIn: { width: '100%', marginTop: 6, background: 'var(--ink, #08111f)', border: '1px solid rgba(184,150,62,.2)', borderRadius: 6, color: 'var(--text, #dde8f0)', fontSize: 12, padding: '5px 8px', outline: 'none', boxSizing: 'border-box' },
  roundsBtn: { background: 'rgba(184,150,62,.12)', border: '1px solid rgba(184,150,62,.3)', borderRadius: 8, color: 'var(--gold2, #d4b06a)', fontSize: 12, fontWeight: 600, padding: '7px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  group: {},
  groupHead: { fontSize: 11, fontWeight: 700, color: 'var(--muted, #8a97a6)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 },
  chevron: { display: 'inline-block', fontSize: 11, transition: 'transform .15s', color: 'var(--muted, #8a97a6)' },
  groupCount: { background: 'var(--ink, #0b0f14)', borderRadius: 10, padding: '1px 7px', fontSize: 11 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer', marginBottom: 5 },
  rowActive: { borderColor: 'var(--gold2, #d4b06a)' },
  checkCluster: { display: 'flex', gap: 4, flexShrink: 0 },
  ck: { width: 26, height: 26, borderRadius: 6, border: '1px solid var(--border2)', background: 'transparent', color: 'var(--muted, #8a97a6)', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: { fontSize: 14, color: 'var(--text, #cdd6e0)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontSize: 11, color: 'var(--muted, #8a97a6)', display: 'flex', gap: 5, marginTop: 1 },
  expChip: { fontSize: 11, fontWeight: 600, border: '1px solid', borderRadius: 20, padding: '2px 9px', flexShrink: 0 },
  rowDel: { background: 'none', border: 'none', color: 'var(--muted, #8a97a6)', cursor: 'pointer', fontSize: 17, flexShrink: 0 },
  empty: { color: 'var(--muted, #8a97a6)', fontSize: 13, padding: '20px 4px', textAlign: 'center' },
  detail: { width: 320, flexShrink: 0, background: 'var(--hull, #162840)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 16, overflowY: 'auto' },
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  detailKicker: { fontSize: 11, fontWeight: 700, color: 'var(--muted, #8a97a6)', textTransform: 'uppercase', letterSpacing: .5 },
  detailClose: { background: 'none', border: 'none', color: 'var(--muted, #8a97a6)', cursor: 'pointer', fontSize: 20 },
  dName: { width: '100%', boxSizing: 'border-box', background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text, #cdd6e0)', fontSize: 15, fontWeight: 600, marginBottom: 14 },
  field: { marginBottom: 12 },
  fieldLabel: { display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted, #8a97a6)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 },
  in: { width: '100%', boxSizing: 'border-box', background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 10px', color: 'var(--text, #cdd6e0)', fontSize: 13, colorScheme: 'dark' },
  row2: { display: 'flex', gap: 8 },
  area: { width: '100%', boxSizing: 'border-box', minHeight: 70, background: 'var(--ink, #0b0f14)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 10px', color: 'var(--text, #cdd6e0)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' },
  delBtn: { width: '100%', background: 'rgba(192,67,74,.12)', color: '#b4313c', border: '0.5px solid rgba(192,67,74,.3)', borderRadius: 8, padding: '9px 0', cursor: 'pointer', fontSize: 13, marginTop: 6 },
  modalWrap: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  expMenu: { position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60, width: 250, background: 'var(--ink2, #0d1e35)', border: '1px solid var(--border, rgba(184,150,62,0.2))', borderRadius: 9, padding: 5, boxShadow: '0 8px 24px rgba(0,0,0,.5)' },
  expOpt: { padding: '9px 10px', borderRadius: 7, cursor: 'pointer' },
  expOptT: { fontSize: 13, color: 'var(--text, #dde8f0)', fontWeight: 600 },
  expOptS: { fontSize: 11, color: 'var(--muted, #8a97a6)', marginTop: 2 },
  printWrap: { position: 'fixed', top: 0, left: 0, width: 960, zIndex: -1, background: '#fff', pointerEvents: 'none' },
  modal: { width: 440, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', background: 'var(--hull, #162840)', border: '0.5px solid var(--border2)', borderRadius: 12, padding: 18 },
}


function LsaDropdown({ value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const selected = options.find(o => o.value === value)
  return (
    <div ref={ref} style={lds.wrap}>
      <button style={lds.trigger} onClick={() => setOpen(o => !o)}>
        <span style={{ color: selected ? 'var(--text, #dde8f0)' : 'var(--muted, #8a97a6)' }}>{selected ? selected.label : placeholder}</span>
        <span style={{ ...lds.caret, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={lds.menu}>
          {options.map(o => (
            <div key={o.value} style={{ ...lds.opt, ...(o.value === value ? lds.optSel : {}) }} onClick={() => { onChange(o.value); setOpen(false) }}>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
const lds = {
  wrap: { position: 'relative' },
  trigger: { boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--hull, #162840)', border: '1px solid var(--border, rgba(184,150,62,0.2))', borderRadius: 8, padding: '9px 12px', color: 'var(--text, #dde8f0)', fontSize: 13, cursor: 'pointer', minWidth: 140 },
  caret: { color: 'var(--muted, #8a97a6)', fontSize: 10, transition: 'transform .15s' },
  menu: { position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50, minWidth: 160, background: 'var(--ink2, #0d1e35)', border: '1px solid var(--border, rgba(184,150,62,0.2))', borderRadius: 8, padding: 4, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.5)' },
  opt: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, color: 'var(--text, #dde8f0)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  optSel: { background: 'rgba(184,150,62,.16)', color: 'var(--gold2, #d4b06a)' },
}
